import { PenLineIcon, EllipsisVerticalIcon, Trash2Icon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

import { useRepresentative } from '../model';

type Props = {
  id: string;
  suggestType?: 'add' | 'edit' | 'remove';
};

const RepresentativeTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownMenuItem>
          <Trash2Icon />
          Delete
        </DropdownMenuItem>
      </TooltipTrigger>
      <TooltipContent side="top" align="center" className="max-w-62.5">
        This is the last active representative or signatory
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const RepresentativeActions = ({ id, suggestType }: Props) => {
  const { editRepresentative, removeRepresentative, undoRepresentative, changesRequested, representatives } =
    useRepresentative();

  const activeSignatories = representatives.filter(({ signatory }) => signatory && suggestType !== 'add');
  const isLastActiveSignatory =
    activeSignatories.length === 1 && activeSignatories.some(({ id: itemId }) => id === itemId);
  const isLastRepresentative =
    representatives.length === 1 || representatives.filter((rep) => rep.suggestType !== 'remove').length === 1;

  const onEdit = () => editRepresentative(id);
  const onRemove = () => removeRepresentative(id);
  const onUndo = () => undoRepresentative?.(id);

  if (suggestType === 'edit' || suggestType === 'remove')
    return (
      <Button variant="link" size="sm" onClick={onUndo}>
        Undo
      </Button>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <EllipsisVerticalIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end" className="w-max">
        <DropdownMenuItem className="py-2 cursor-pointer" onClick={onEdit}>
          <PenLineIcon />
          Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {changesRequested && (isLastActiveSignatory || isLastRepresentative) ? (
          <RepresentativeTooltip />
        ) : (
          <DropdownMenuItem variant="destructive" className="py-2 cursor-pointer" onClick={onRemove}>
            <Trash2Icon />
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RepresentativeActions;
